import React, { useMemo, useState } from 'react'
import moment from 'moment'
import {
  Avatar,
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import NotesIcon from '@mui/icons-material/Notes'
import CallIcon from '@mui/icons-material/Call'
import EmailIcon from '@mui/icons-material/Email'
import GroupsIcon from '@mui/icons-material/Groups'
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest'
import { formatDateTime, initials } from './helpers'

export const ACTIVITY_KINDS = [
  { key: 'note', label: 'Note', icon: <NotesIcon fontSize="small" />, color: '#64748B' },
  { key: 'call', label: 'Call', icon: <CallIcon fontSize="small" />, color: '#268EBB' },
  { key: 'email', label: 'Email', icon: <EmailIcon fontSize="small" />, color: '#85276A' },
  { key: 'meeting', label: 'Meeting', icon: <GroupsIcon fontSize="small" />, color: '#00964C' },
  { key: 'system', label: 'System', icon: <SettingsSuggestIcon fontSize="small" />, color: '#94A3B8' },
]

const kindMeta = (key) => ACTIVITY_KINDS.find((kind) => kind.key === key) ?? ACTIVITY_KINDS[0]

// Timeline of CRM comments/activities with a composer on top. `onSubmit`
// receives { kind, note } and must return a promise; the caller owns the list.
// A system entry may have no author (it was written by a scheduled job).
const ActivityTimeline = ({
  items = [],
  onSubmit,
  placeholder = 'Add a note, or log a call, email or meeting…',
  emptyText = 'No activity yet.',
  extraControls,
}) => {
  const [kind, setKind] = useState('note')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const sorted = useMemo(
    () =>
      [...items]
        .filter(Boolean)
        .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()),
    [items],
  )

  const submit = async () => {
    if (!note.trim() || saving) return
    setSaving(true)
    try {
      await onSubmit({ kind, note: note.trim() })
      setNote('')
      setKind('note')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap={2}>
      {onSubmit && (
        <Stack gap={1}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={kind}
              onChange={(event, value) => value && setKind(value)}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 1.25,
                  gap: 0.5,
                  borderColor: '#E2E8F0',
                },
              }}
            >
              {ACTIVITY_KINDS.filter((option) => option.key !== 'system').map((option) => (
                <ToggleButton key={option.key} value={option.key}>
                  {option.icon}
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {extraControls}
          </Stack>
          <TextField
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={placeholder}
            multiline
            minRows={2}
            fullWidth
            size="small"
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') submit()
            }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {kind === 'note'
                ? 'Notes stay on the timeline.'
                : `Logging a ${kindMeta(kind).label.toLowerCase()} also marks the lead as contacted today.`}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={submit}
              disabled={!note.trim() || saving}
            >
              {kind === 'note' ? 'Add note' : `Log ${kindMeta(kind).label.toLowerCase()}`}
            </Button>
          </Stack>
        </Stack>
      )}

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      ) : (
        <Stack gap={0}>
          {sorted.map((item, index) => {
            const meta = kindMeta(item.kind)
            const authorName = [item.author?.firstName, item.author?.lastName]
              .filter(Boolean)
              .join(' ')
            // Entries raised by a scheduled job (e.g. the nightly usage
            // reconciliation) have no author: say so rather than leave a gap.
            const automatic = !authorName && (meta.key === 'system' || item.systemComment)
            return (
              <Stack
                key={item._id ?? index}
                direction="row"
                gap={1.5}
                sx={{
                  position: 'relative',
                  pb: 2,
                  '&:not(:last-of-type)::before': {
                    content: '""',
                    position: 'absolute',
                    left: 15,
                    top: 32,
                    bottom: 0,
                    width: 2,
                    bgcolor: '#EEF2F7',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: alpha(meta.color, 0.12),
                    color: meta.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                >
                  {meta.icon}
                </Box>
                <Box minWidth={0} flex={1}>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                      {meta.key === 'note' ? 'Note' : meta.label}
                    </Typography>
                    {authorName && (
                      <Stack direction="row" gap={0.5} alignItems="center">
                        <Avatar
                          src={item.author?.photograph}
                          sx={{ width: 18, height: 18, fontSize: 10 }}
                        >
                          {initials(authorName)}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          {authorName}
                        </Typography>
                      </Stack>
                    )}
                    {automatic && (
                      <Typography variant="caption" color="text.secondary">
                        Automatic - no member of staff
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {formatDateTime(item.createdAt)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}
                  >
                    {item.comment ?? item.note ?? ''}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}

export default ActivityTimeline
